// src/components/Projects/ProjectDetails.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios, { AxiosError } from "axios";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  Alert,
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Pagination,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FolderIcon from "@mui/icons-material/Folder";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import CommentIcon from "@mui/icons-material/Comment";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import RefreshIcon from "@mui/icons-material/Refresh";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SortIcon from "@mui/icons-material/Sort";
import AddIcon from "@mui/icons-material/Add";
import { baseUrl } from "../../constants";
import { getUserInfoFromToken } from "../../utils/Main";
import { handleDateWithTime } from "../../utils/date-handlers";
import { getApiErrorMessage } from "../../utils/apiError";

import { TaskStatus } from "../../enums/task-status.enum";
import { TaskImportance } from "../../enums/task-importance.enum";
import {
  getTaskStatusContainerBgColor,
  getTaskStatusContainerTextColor,
  getTaskStatusLabel,
} from "../../utils/task-status.utils";
import {
  getImportanceContainerColor,
  getImportanceContainerText,
} from "../../utils/task-importance.utils";

/* ─────────────────────────── Types ─────────────────────────── */

type TaskVisibility = "PRIVATE" | "PROJECT" | "DEPARTMENT" | "WORKSPACE";
const VISIBILITY_OPTIONS: TaskVisibility[] = [
  "PRIVATE",
  "PROJECT",
  "DEPARTMENT",
  "WORKSPACE",
];

type ProfileLite = { imageUrl?: string | null; name?: string | null } | null;

type MemberLite = {
  id: number;
  username: string;
  profile?: ProfileLite;
};

type Dept = { id: number; name: string };

type ProjectDTO = {
  id: number;
  name: string;
  description?: string | null;
  startDate?: string | null;
  deadline?: string | null;
  status?: string | null;
  priority?: string | null;
  tags?: string[] | null;
  archived?: boolean;
  defaultVisibility?: string;
  department?: Dept | null;
};

type UserProjectsItem = ProjectDTO & {
  userProjects?: Array<{
    user: { id: number; username: string; profile?: ProfileLite };
  }>;
};

type UserLite = {
  id: number;
  username?: string;
  profile?: { imageUrl?: string | null } | null;
} | null;

type TaskLite = {
  id: number;
  title: string;
  description?: string | null;
  ref?: string | null;
  status: TaskStatus | string;
  importance: TaskImportance | string;
  startDate: string;
  endDate: string;
  visibility?: TaskVisibility | string | null; // ⬅️ added
  projectId?: number; // ⬅️ used for hard project scoping
  remindIn?: string | null;
  remindAt?: string | null;
  reminded?: boolean;
  assignedUsers?: Array<{ user: UserLite } | UserLite>;
  followers?: Array<{ user: UserLite } | UserLite>;
  createdBy?: UserLite;
  _count?: {
    taskComments?: number;
    taskAttachments?: number;
    subTasks?: number;
  };
};

type MembersResponse = Array<{
  user: { id: number; username: string; profile?: ProfileLite };
  projectId: number;
}>;

/* ─────────────────────── Constants / Helpers ─────────────────────── */

const tokenHeader = () => {
  const t =
    localStorage.getItem("accessToken") || localStorage.getItem("token");
  return { Authorization: `Bearer ${t}` };
};

function reminderText(t: TaskLite) {
  if (t.remindIn === "custom") {
    return t.remindAt ? `At ${handleDateWithTime(t.remindAt)}` : "Custom";
  }
  if (t.remindIn) return `Before ${t.remindIn} (deadline)`;
  return null;
}

function normStatus(s: TaskLite["status"]): TaskStatus {
  return String(s || "").toUpperCase() as TaskStatus;
}
function normImportance(i: TaskLite["importance"]): TaskImportance {
  return String(i || "").toUpperCase() as TaskImportance;
}
function normVisibility(v: TaskLite["visibility"]): TaskVisibility | "" {
  const up = String(v || "").toUpperCase();
  return (
    VISIBILITY_OPTIONS.includes(up as TaskVisibility)
      ? (up as TaskVisibility)
      : ""
  ) as TaskVisibility | "";
}

// ——— User display helpers ———
const getUserImageUrl = (u: any): string | undefined =>
  u?.profile?.imageUrl ??
  u?.imageUrl ??
  u?.user?.profile?.imageUrl ??
  u?.user?.imageUrl ??
  u?.profileImage ??
  u?.avatarUrl ??
  undefined;

const getUserDisplayName = (u: any): string =>
  u?.profile?.name ||
  u?.username ||
  (typeof u?.id === "number" ? `User #${u.id}` : "User");

const DONE_LIKE = new Set([
  "DONE",
  "COMPLETED",
  "CLOSED",
  "RESOLVED",
  "FINISHED",
]);

const importanceRank = (imp: TaskImportance) => {
  const map: Record<string, number> = {
    CRITICAL: 3,
    HIGH: 2,
    MEDIUM: 1,
    LOW: 0,
  };
  return map[String(imp).toUpperCase()] ?? 0;
};

type SortKey =
  | "dueDesc"
  | "dueAsc"
  | "startAsc"
  | "startDesc"
  | "title"
  | "importanceDesc"
  | "importanceAsc";

type InfoFlags = {
  ref: boolean;
  description: boolean;
  comments: boolean;
  reminders: boolean;
  followers: boolean;
  attachments: boolean;
  subtasks: boolean;
};

/* ─────────────────────────── Component ─────────────────────────── */

const ProjectDetails: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const pid = Number(projectId || 0);

  const user = useMemo(() => getUserInfoFromToken(), []);
  const currentUserId = user?.id;
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  // view mode persisted for admins
  const initialView = (
    isAdmin
      ? (localStorage.getItem("project_view_mode") as "own" | "all") || "own"
      : "own"
  ) as "own" | "all";
  const [viewMode, setViewMode] = useState<"own" | "all">(initialView);

  // main data
  const [project, setProject] = useState<ProjectDTO | null>(null);
  const [loading, setLoading] = useState(true);

  // members
  const [members, setMembers] = useState<MemberLite[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearchRaw, setMemberSearchRaw] = useState("");
  const [memberSearch, setMemberSearch] = useState("");

  // tasks
  const [tasks, setTasks] = useState<TaskLite[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);

  // filters
  const [qRaw, setQRaw] = useState("");
  const [q, setQ] = useState("");
  const statusOptions = useMemo(
    () =>
      (Object.values(TaskStatus) as TaskStatus[]).filter(
        (s) => String(s).toLowerCase() !== "all"
      ),
    []
  );
  const importanceOptions = useMemo(
    () => Object.values(TaskImportance) as TaskImportance[],
    []
  );
  const [selStatuses, setSelStatuses] = useState<TaskStatus[]>([]);
  const [selImportances, setSelImportances] = useState<TaskImportance[]>([]);
  const [selVisibilities, setSelVisibilities] = useState<TaskVisibility[]>([]); // ⬅️ NEW
  const [filtersOpen, setFiltersOpen] = useState(true);

  // info flags (persisted per project)
  const flagsKey = (id: number) => `project_task_info_flags_v1_${id}`;
  const defaultFlags: InfoFlags = useMemo(
    () => ({
      ref: true,
      description: false,
      comments: true,
      reminders: true,
      followers: false,
      attachments: true,
      subtasks: true,
    }),
    []
  );
  const [infoFlags, setInfoFlags] = useState<InfoFlags>(() => {
    try {
      const raw = localStorage.getItem(flagsKey(pid));
      return raw ? { ...defaultFlags, ...JSON.parse(raw) } : defaultFlags;
    } catch {
      return defaultFlags;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(flagsKey(pid), JSON.stringify(infoFlags));
    } catch {}
  }, [pid, infoFlags]);

  // sorting
  const [sortKey, setSortKey] = useState<SortKey>("dueDesc");

  // chat & dialogs
  const [chatLoading, setChatLoading] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  // snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // abort controllers
  const tasksAbort = useRef<AbortController | null>(null);
  const membersAbort = useRef<AbortController | null>(null);

  // ⬇️ Pagination state (client-side over filtered tasks)
  const PAGE_SIZE = 50;
  const [page, setPage] = useState<number>(1);

  /* ─────────── Persist admin view mode ─────────── */
  useEffect(() => {
    if (isAdmin) {
      localStorage.setItem("project_view_mode", viewMode);
    }
  }, [viewMode, isAdmin]);

  /* ─────────── Debounce search (tasks & members) ─────────── */
  useEffect(() => {
    const t = setTimeout(() => setQ(qRaw.trim()), 300);
    return () => clearTimeout(t);
  }, [qRaw]);
  useEffect(() => {
    const t = setTimeout(() => setMemberSearch(memberSearchRaw.trim()), 300);
    return () => clearTimeout(t);
  }, [memberSearchRaw]);

  /* ─────────── Reset pagination when filters/sort change ─────────── */
  useEffect(() => {
    setPage(1);
  }, [q, selStatuses, selImportances, selVisibilities, sortKey]);

  /* ─────────── Load project (admin or fallback user scope) ─────────── */
  const loadProject = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      // Admin endpoint (includes department)
      const { data } = await axios.get<ProjectDTO>(
        `${baseUrl}/projects/${pid}`,
        {
          headers: tokenHeader(),
        }
      );
      setProject(data);
    } catch (e) {
      const err = e as AxiosError;
      // Fallback to user-scoped list
      if (
        err.response?.status === 401 ||
        err.response?.status === 403 ||
        err.response?.status === 404
      ) {
        try {
          const { data } = await axios.get<UserProjectsItem[]>(
            `${baseUrl}/projects/user/projects`,
            { headers: tokenHeader() }
          );
          const found = (data || []).find((p) => p.id === pid) || null;
          if (!found) throw new Error("Project not found or not accessible");
          setProject(found);
        } catch (e2) {
          setSnackbar({
            open: true,
            message: getApiErrorMessage(e2, "Failed to load project"),
            severity: "error",
          });
          setProject(null);
        }
      } else {
        setSnackbar({
          open: true,
          message: getApiErrorMessage(err, "Failed to load project"),
          severity: "error",
        });
        setProject(null);
      }
    } finally {
      setLoading(false);
    }
  }, [pid]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  /* ─────────── Load members ─────────── */
  const loadMembers = useCallback(async () => {
    if (!pid) return;
    membersAbort.current?.abort();
    const ctrl = new AbortController();
    membersAbort.current = ctrl;

    setMembersLoading(true);
    try {
      const { data } = await axios.get<MembersResponse | any>(
        `${baseUrl}/projects/project-users/${pid}`,
        {
          headers: tokenHeader(),
          params: { name: memberSearch || undefined, page: 1, limit: 200 },
          signal: ctrl.signal,
        }
      );

      const flat: MemberLite[] = (Array.isArray(data) ? data : []).map(
        (row: any) => {
          const u = row?.user ?? row;
          const imageUrl = u?.profile?.imageUrl ?? row?.imageUrl ?? null;
          const name = u?.profile?.name ?? row?.name ?? null;
          return {
            id: Number(u?.id),
            username: String(u?.username ?? ""),
            profile: { imageUrl, name },
          };
        }
      );
      setMembers(flat.filter((m) => !!m.id));
    } catch {
      // Hide access errors; show empty
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, [pid, memberSearch]);

  useEffect(() => {
    loadMembers();
    return () => membersAbort.current?.abort();
  }, [loadMembers]);

  /* ─────────── Load tasks (via /tasks?projectIds=) ─────────── */
  const loadTasks = useCallback(async () => {
    if (!pid) return;
    
    // Prevent multiple simultaneous calls by checking loading state
    if (tasksLoading) return;
    
    // Abort any existing request
    if (tasksAbort.current) {
      tasksAbort.current.abort();
    }
    
    const ctrl = new AbortController();
    tasksAbort.current = ctrl;

    setTasksLoading(true);
    try {
      const effectiveView = isAdmin ? viewMode : "own";

      // Build params for the /tasks listing endpoint
      const params: any = {
        page: 1,
        limit: 10000, // client-side paging
        projectIds: pid, // filter by this project (server)
      };

      // If not "all", show only tasks assigned to current user
      if (effectiveView === "own") {
        const uid = getUserInfoFromToken()?.id;
        if (uid) params.assignedUserIds = uid;
      }

      const { data } = await axios.get(`${baseUrl}/tasks`, {
        headers: tokenHeader(),
        params,
        signal: ctrl.signal,
      });

      setTasks(Array.isArray(data?.tasks) ? data.tasks : []);
    } catch (e) {
      // Only show error if it's not an abort error or canceled error
      if ((e as any)?.name !== 'AbortError' && (e as any)?.name !== 'CanceledError') {
        setSnackbar({
          open: true,
          message:
            (e as any)?.response?.data?.message || "Couldn't load project tasks",
          severity: "error",
        });
        setTasks([]);
      }
    } finally {
      setTasksLoading(false);
      // Clear the abort controller reference when done
      if (tasksAbort.current === ctrl) {
        tasksAbort.current = null;
      }
    }
  }, [pid, viewMode, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadTasks();
    return () => tasksAbort.current?.abort();
  }, [loadTasks]);

  /* ─────────── Visibility scoping (project + user rule) ─────────── */
  const visibilityScopedTasks = useMemo(() => {
    const uid = currentUserId;
    
    // Since the API call already filtered by projectIds, we can trust all returned tasks belong to this project
    // The projectId field might be undefined due to backend response structure, but the API filtering ensures correctness

    const isAssignee = (t: TaskLite) => {
      const raw = (t.assignedUsers || []) as any[];
      const flat = raw
        .map((a) => (a?.user ?? a) || null)
        .filter((u) => u && typeof u.id === "number") as Array<{ id: number }>;
      return !!uid && flat.some((u) => u.id === uid);
    };

    const isCreator = (t: TaskLite) => {
      const c: any = t.createdBy || null;
      const cid = typeof c?.id === "number" ? c.id : undefined;
      return !!uid && cid === uid;
    };

    const isProjectVisible = (t: TaskLite) =>
      normVisibility(t.visibility) === "PROJECT";

    // Since API already filtered by project, just apply visibility rules
    const result = (tasks || []).filter((t) => {
      const assignee = isAssignee(t);
      const creator = isCreator(t);
      const projectVisible = isProjectVisible(t);
      
      // If user is assigned or creator, show the task (regardless of visibility)
      // If task has PROJECT visibility, show it to project members
      return assignee || creator || projectVisible;
    });
    
    return result;
  }, [tasks, pid, currentUserId]);

  /* ─────────── Derived: filtered + sorted tasks ─────────── */
  const filteredTasks = useMemo(() => {
    const lower = q.toLowerCase();

    const base = (visibilityScopedTasks || []).filter((t) => {
      if (lower) {
        const hay = `${t.title || ""} ${t.description || ""} ${
          t.ref || ""
        }`.toLowerCase();
        if (!hay.includes(lower)) return false;
      }
      const st = normStatus(t.status);
      const imp = normImportance(t.importance);
      const vis = normVisibility(t.visibility);

      if (selStatuses.length && !selStatuses.includes(st)) return false;
      if (selImportances.length && !selImportances.includes(imp)) return false;
      if (selVisibilities.length && (!vis || !selVisibilities.includes(vis)))
        return false;

      return true;
    });

    const sorted = [...base].sort((a, b) => {
      const da = dayjs(a.endDate).valueOf();
      const db = dayjs(b.endDate).valueOf();
      const sa = dayjs(a.startDate).valueOf();
      const sb = dayjs(b.startDate).valueOf();

      switch (sortKey) {
        case "dueAsc":
          return da - db;
        case "dueDesc":
          return db - da;
        case "startAsc":
          return sa - sb;
        case "startDesc":
          return sb - sa;
        case "title":
          return String(a.title).localeCompare(String(b.title));
        case "importanceDesc":
          return (
            importanceRank(normImportance(b.importance)) -
            importanceRank(normImportance(a.importance))
          );
        case "importanceAsc":
          return (
            importanceRank(normImportance(a.importance)) -
            importanceRank(normImportance(b.importance))
          );
        default:
          return 0;
      }
    });

    return sorted;
  }, [
    visibilityScopedTasks,
    q,
    selStatuses,
    selImportances,
    selVisibilities,
    sortKey,
  ]);

  // ⬇️ Paged slice of filtered tasks
  const totalPages = Math.max(1, Math.ceil(filteredTasks.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const startIdx = (currentPage - 1) * PAGE_SIZE;
  const endIdx = Math.min(startIdx + PAGE_SIZE, filteredTasks.length);
  const pagedTasks = filteredTasks.slice(startIdx, endIdx);

  /* ─────────── Stats (header widgets) ─────────── */
  const stats = useMemo(() => {
    const total = filteredTasks.length; // reflect scoped set
    const now = dayjs();
    let done = 0;
    let overdue = 0;

    filteredTasks.forEach((t) => {
      const st = normStatus(t.status);
      if (DONE_LIKE.has(String(st))) done += 1;
      if (!DONE_LIKE.has(String(st)) && dayjs(t.endDate).isBefore(now)) {
        overdue += 1;
      }
    });

    const progress = total ? Math.round((done / total) * 100) : 0;
    return { total, done, overdue, progress };
  }, [filteredTasks]);

  /* ─────────── Actions ─────────── */
  const onOpenChat = async () => {
    if (!pid) return;
    setChatLoading(true);
    try {
      const { data } = await axios.post<{ channelId: number }>(
        `${baseUrl}/chat/resolve/project/${pid}`,
        {},
        { headers: tokenHeader() }
      );
      navigate(`/chat/${data.channelId}`);
    } catch (e: any) {
      setSnackbar({
        open: true,
        message:
          e?.response?.data?.message ||
          "Could not open project chat. Check your access and try again.",
        severity: "error",
      });
    } finally {
      setChatLoading(false);
    }
  };

  const handleCloseSnackbar = () => setSnackbar((p) => ({ ...p, open: false }));

  const clearFilters = () => {
    setQRaw("");
    setSelStatuses([]);
    setSelImportances([]);
    setSelVisibilities([]); // ⬅️ clear visibility selection
  };

  const copyLink = () => {
    try {
      const url = window.location.href;
      navigator.clipboard.writeText(url);
      setSnackbar({
        open: true,
        message: "Link copied to clipboard",
        severity: "success",
      });
    } catch {
      setSnackbar({ open: true, message: "Copy failed", severity: "error" });
    }
  };

const exportExcel = async () => {
  try {
    // Build rows from the *filtered* tasks (keeps current filters)
    const rows = filteredTasks.map((t) => {
      const st = normStatus(t.status);
      const imp = normImportance(t.importance);
      const vis = normVisibility(t.visibility);

      const assignees = ((t.assignedUsers || []) as any[])
        .map((a: any) => (a?.user ?? a)?.username)
        .filter(Boolean)
        .join(", ");

      const createdByName =
        (t.createdBy as any)?.username ||
        (typeof (t.createdBy as any)?.id === "number"
          ? `#${(t.createdBy as any)?.id}`
          : "");

      return {
        id: t.id,
        ref: t.ref || "",
        title: t.title || "",
        status: getTaskStatusLabel(st),
        importance: getImportanceContainerText(imp),
        visibility: vis || "",
        startDate: handleDateWithTime(t.startDate),
        endDate: handleDateWithTime(t.endDate),
        assignees,
        comments: t._count?.taskComments ?? 0,
        attachments: t._count?.taskAttachments ?? 0,
        subtasks: t._count?.subTasks ?? 0,
        reminder: ((): string => {
          if (t.remindIn === "custom") {
            return t.remindAt ? `At ${handleDateWithTime(t.remindAt)}` : "Custom";
          }
          if (t.remindIn) return `Before ${t.remindIn} (deadline)`;
          return "";
        })(),
        createdBy: createdByName,
      };
    });

    if (!rows.length) {
      setSnackbar({ open: true, message: "No rows to export.", severity: "info" });
      return;
    }

    // Dynamic import keeps bundle small
    const mod: any = await import("xlsx");
    const XLSX = mod.default ?? mod;

    // Keep header order explicit
    const headers = [
      "id",
      "ref",
      "title",
      "status",
      "importance",
      "visibility",
      "startDate",
      "endDate",
      "assignees",
      "comments",
      "attachments",
      "subtasks",
      "reminder",
      "createdBy",
    ] as const;

    const ws = XLSX.utils.json_to_sheet(rows, { header: headers as any });

    // Autosize columns (Arabic text measured as UTF-16 length works fine)
    (ws as any)["!cols"] = headers.map((h) => {
      const maxLen = Math.max(
        String(h).length,
        ...rows.map((r) => String((r as any)[h] ?? "").length)
      );
      return { wch: Math.min(Math.max(maxLen + 2, 10), 60) };
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tasks");

    const safeName = (project?.name || "project").replace(/[\\/:*?"<>|]/g, "_");
    XLSX.writeFile(wb, `${safeName}-tasks.xlsx`);

    setSnackbar({ open: true, message: "Exported to Excel.", severity: "success" });
  } catch (e) {
    setSnackbar({ open: true, message: "Excel export failed.", severity: "error" });
  }
};


  const refreshAll = useCallback(() => {
    loadProject();
    loadMembers();
    loadTasks();
  }, [loadProject, loadMembers, loadTasks]);

  /* ─────────── UI ─────────── */

  if (loading) {
    return (
      <Box
        p={3}
        display="flex"
        flexDirection="column"
        height="100vh"
        alignItems="center"
        justifyContent="center"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Loading project details...
        </Typography>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box p={3}>
        <Typography variant="h6">Project not found.</Typography>
        <Button
          sx={{ mt: 1 }}
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  const statusChip = project.status ? (
    <Chip size="small" label={project.status} />
  ) : null;
  const priorityChip = project.priority ? (
    <Chip size="small" label={project.priority} />
  ) : null;
  const visChip = project.defaultVisibility ? (
    <Chip size="small" variant="outlined" label={project.defaultVisibility} />
  ) : null;

  const filtersActive = Boolean(
    q || selStatuses.length || selImportances.length || selVisibilities.length
  );

  return (
    <Box sx={{ p: 2 }}>

      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        gap={1}
        flexWrap="wrap"
      >
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon sx={{ color: "#5DB0FF" }} />
          </IconButton>

          <FolderIcon color="action" />
          <Typography variant="h5" fontWeight={700}>
            {project.name}
          </Typography>

          {project.archived && (
            <Chip label="Archived" size="small" color="default" />
          )}
          {statusChip}
          {priorityChip}
          {visChip}

          <Tooltip title="Project info">
            <IconButton onClick={() => setInfoOpen(true)}>
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>

          <Button
            size="small"
            variant="outlined"
            startIcon={<ForumOutlinedIcon />}
            onClick={onOpenChat}
            disabled={chatLoading}
          >
            {chatLoading ? "Opening…" : "Chat"}
          </Button>
        </Box>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {/* Add Task for this project */}
          <Tooltip title="Add a task to this project">
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() =>
                  navigate(`/new-task/0?type=Task&projectId=${pid}`, {
                    state: { projectId: pid },
                  })
                }
              >
                Add Task
              </Button>
            </span>
          </Tooltip>

          <Tooltip title="Copy link">
            <IconButton onClick={copyLink}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open in new tab">
            <span>
              <IconButton
                onClick={() => window.open(window.location.href, "_blank")}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={refreshAll}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
         <Tooltip title="Export to Excel">
  <span>
    <IconButton
      onClick={exportExcel}
      disabled={filteredTasks.length === 0}
    >
      <FileDownloadIcon />
    </IconButton>
  </span>
</Tooltip>

        </Box>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    flexWrap="wrap"
                  >
                    {project.startDate && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CalendarTodayIcon fontSize="small" />
                        <Typography variant="caption" color="text.secondary">
                          Start: {handleDateWithTime(project.startDate)}
                        </Typography>
                      </Box>
                    )}
                    {project.deadline && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <AccessTimeIcon fontSize="small" />
                        <Typography variant="caption" color="text.secondary">
                          Deadline: {handleDateWithTime(project.deadline)}
                        </Typography>
                      </Box>
                    )}
                    {project.department && (
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <BusinessIcon fontSize="small" />
                        <Typography variant="caption" color="text.secondary">
                          Dept: {project.department.name}
                        </Typography>
                      </Box>
                    )}

                    {isAdmin && (
                      <TextField
                        select
                        size="small"
                        label="Show"
                        value={viewMode}
                        onChange={(e) =>
                          setViewMode(e.target.value as "own" | "all")
                        }
                        sx={{ width: 150 }}
                      >
                        <MenuItem value="own">My tasks</MenuItem>
                        <MenuItem value="all">All tasks</MenuItem>
                      </TextField>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={0.5}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Progress ({stats.done}/{stats.total})
                      </Typography>
                      {!!stats.overdue && (
                        <Chip
                          size="small"
                          color="error"
                          label={`${stats.overdue} overdue`}
                        />
                      )}
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={stats.progress}
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Members */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ py: 2, height: "100%" }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={1}
                gap={1}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <GroupIcon fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Members
                  </Typography>
                  <Chip size="small" label={members.length} />
                </Box>

                <TextField
                  value={memberSearchRaw}
                  onChange={(e) => setMemberSearchRaw(e.target.value)}
                  size="small"
                  placeholder="Search member…"
                  sx={{ width: 220 }}
                />
              </Box>

              {membersLoading ? (
                <Box sx={{ py: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={24} />
                  <Typography variant="caption" color="text.secondary">
                    Loading members...
                  </Typography>
                </Box>
              ) : members.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No visible members or you don’t have access.
                </Typography>
              ) : (
                <AvatarGroup
                  max={10}
                  sx={{
                    "& .MuiAvatar-root": {
                      width: 28,
                      height: 28,
                      fontSize: 13,
                    },
                  }}
                >
                  {members.map((m) => {
                    const displayName = m.profile?.name || m.username;
                    return (
                      <Tooltip key={m.id} title={displayName}>
                        <Avatar src={m.profile?.imageUrl || undefined}>
                          {(displayName || "?").charAt(0).toUpperCase()}
                        </Avatar>
                      </Tooltip>
                    );
                  })}
                </AvatarGroup>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters & Controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
          >
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <Button
                size="small"
                startIcon={<FilterListIcon />}
                onClick={() => setFiltersOpen((v) => !v)}
              >
                {filtersOpen ? "Hide filters" : "Show filters"}
              </Button>

              {filtersActive && (
                <Chip
                  size="small"
                  variant="outlined"
                  color="info"
                  label="Filters active"
                />
              )}

              {filtersActive && (
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<ClearAllIcon />}
                  onClick={clearFilters}
                >
                  Clear
                </Button>
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              {/* Info visibility toggles */}
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {(
                  [
                    ["ref", "Ref"],
                    ["description", "Description"],
                    ["comments", "Comments"],
                    ["reminders", "Reminders"],
                    ["followers", "Followers"],
                    ["attachments", "Attachments"],
                    ["subtasks", "Subtasks"],
                  ] as Array<[keyof InfoFlags, string]>
                ).map(([k, label]) => (
                  <Chip
                    key={k}
                    size="small"
                    variant={infoFlags[k] ? "filled" : "outlined"}
                    color={infoFlags[k] ? "primary" : "default"}
                    label={label}
                    onClick={() => setInfoFlags((f) => ({ ...f, [k]: !f[k] }))}
                    sx={{ userSelect: "none" }}
                  />
                ))}
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <SortIcon fontSize="small" />
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={sortKey}
                  onChange={(_, v) => v && setSortKey(v)}
                >
                  <ToggleButton value="dueDesc" title="Due: newest first">
                    Due ↓
                  </ToggleButton>
                  <ToggleButton value="dueAsc" title="Due: oldest first">
                    Due ↑
                  </ToggleButton>
                  <ToggleButton value="startAsc" title="Start: earliest first">
                    Start ↑
                  </ToggleButton>
                  <ToggleButton value="title" title="Alphabetical by title">
                    Title
                  </ToggleButton>
                  <ToggleButton
                    value="importanceDesc"
                    title="Importance: high first"
                  >
                    Importance
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          </Box>

          <Collapse in={filtersOpen} unmountOnExit>
            <Grid container spacing={1.5} alignItems="center" mt={1}>
              <Grid item xs={12} md={4.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Search tasks"
                  placeholder="Title, description, ref…"
                  value={qRaw}
                  onChange={(e) => setQRaw(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3.5}>
                <Autocomplete
                  multiple
                  size="small"
                  options={statusOptions}
                  value={selStatuses}
                  onChange={(_, v) => setSelStatuses(v)}
                  getOptionLabel={(o) => getTaskStatusLabel(o)}
                  renderInput={(params) => (
                    <TextField {...params} label="Status" />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  multiple
                  size="small"
                  options={importanceOptions}
                  value={selImportances}
                  onChange={(_, v) => setSelImportances(v)}
                  getOptionLabel={(o) => getImportanceContainerText(o)}
                  renderInput={(params) => (
                    <TextField {...params} label="Importance" />
                  )}
                />
              </Grid>

              {/* ⬇️ Visibility quick chips (click to toggle) */}
              <Grid item xs={12}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    Visibility:
                  </Typography>
                  {VISIBILITY_OPTIONS.map((v) => {
                    const selected = selVisibilities.includes(v);
                    return (
                      <Chip
                        key={v}
                        size="small"
                        label={v}
                        color={selected ? "primary" : "default"}
                        variant={selected ? "filled" : "outlined"}
                        onClick={() =>
                          setSelVisibilities((prev) =>
                            prev.includes(v)
                              ? prev.filter((x) => x !== v)
                              : [...prev, v]
                          )
                        }
                        sx={{ userSelect: "none" }}
                      />
                    );
                  })}
                </Box>
              </Grid>

              {(selStatuses.length > 0 ||
                selImportances.length > 0 ||
                selVisibilities.length > 0) && (
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    flexWrap="wrap"
                  >
                    {selStatuses.map((s) => (
                      <Chip
                        key={`s-${s}`}
                        size="small"
                        label={`Status: ${getTaskStatusLabel(s)}`}
                        onDelete={() =>
                          setSelStatuses((prev) => prev.filter((x) => x !== s))
                        }
                        sx={{
                          backgroundColor: getTaskStatusContainerBgColor(s),
                          color: getTaskStatusContainerTextColor(s),
                        }}
                      />
                    ))}
                    {selImportances.map((i) => (
                      <Chip
                        key={`i-${i}`}
                        size="small"
                        label={`Importance: ${getImportanceContainerText(i)}`}
                        onDelete={() =>
                          setSelImportances((prev) =>
                            prev.filter((x) => x !== i)
                          )
                        }
                        sx={{
                          backgroundColor: getImportanceContainerColor(i),
                          color: "#fff",
                        }}
                      />
                    ))}
                    {selVisibilities.map((v) => (
                      <Chip
                        key={`v-${v}`}
                        size="small"
                        variant="outlined"
                        label={`Visibility: ${v}`}
                        onDelete={() =>
                          setSelVisibilities((prev) =>
                            prev.filter((x) => x !== v)
                          )
                        }
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Collapse>
        </CardContent>
      </Card>

      {/* Tasks */}
      {tasksLoading ? (
        <Box sx={{ py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Loading tasks...
          </Typography>
        </Box>
      ) : filteredTasks.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              {visibilityScopedTasks.length
                ? "No tasks match your filters."
                : "No visible tasks for this project (based on your permissions)."}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
              gap={1}
              flexWrap="wrap"
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Tasks ({filteredTasks.length})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Showing {filteredTasks.length === 0 ? 0 : startIdx + 1}–{endIdx}{" "}
                of {filteredTasks.length}
              </Typography>
            </Box>

            <Divider sx={{ mb: 1 }} />

            <List dense>
              {pagedTasks.map((t) => {
                const comments = t._count?.taskComments ?? 0;
                const attachments = t._count?.taskAttachments ?? 0;
                const remText = reminderText(t);
                const st = normStatus(t.status);
                const imp = normImportance(t.importance);

                const overdue =
                  !DONE_LIKE.has(String(st)) &&
                  dayjs(t.endDate).isBefore(dayjs());

                return (
                  <ListItem
                    key={t.id}
                    onDoubleClick={() => navigate(`/task/${t.id}`)}
                    sx={{
                      borderRadius: 1,
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                      cursor: "pointer",
                      borderLeft: overdue
                        ? `3px solid ${theme.palette.error.main}`
                        : "3px solid transparent",
                      pl: overdue ? 0.5 : 0,
                    }}
                    secondaryAction={
                      <Box display="flex" alignItems="center" gap={1}>
                        {/* quick glance actions on the right */}
                        {infoFlags.comments && (
                          <Tooltip title="Comments">
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <CommentIcon fontSize="small" />
                              <Typography variant="caption">
                                {comments}
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}

                        {infoFlags.attachments && (
                          <Tooltip title="Attachments">
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AttachFileIcon fontSize="small" />
                              <Typography variant="caption">
                                {attachments}
                              </Typography>
                            </Box>
                          </Tooltip>
                        )}

                        {infoFlags.reminders && remText && (
                          <Tooltip title={remText}>
                            <NotificationsNoneIcon
                              fontSize="small"
                              sx={{ opacity: t.reminded ? 0.5 : 1 }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          flexWrap="wrap"
                        >
                          <Typography variant="body1" fontWeight={600}>
                            {t.title}
                          </Typography>

                          <Chip
                            size="small"
                            label={getTaskStatusLabel(st)}
                            sx={{
                              backgroundColor:
                                getTaskStatusContainerBgColor(st),
                              color: getTaskStatusContainerTextColor(st),
                            }}
                          />

                          <Chip
                            size="small"
                            label={getImportanceContainerText(imp)}
                            sx={{
                              backgroundColor: getImportanceContainerColor(imp),
                              color: "#fff",
                            }}
                          />

                          {overdue && (
                            <Chip size="small" color="error" label="Overdue" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box
                          mt={0.5}
                          display="flex"
                          alignItems="center"
                          gap={2}
                          flexWrap="wrap"
                        >
                          {/* Start / Due */}
                          <Typography variant="caption" color="text.secondary">
                            Start: {handleDateWithTime(t.startDate)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Due: {handleDateWithTime(t.endDate)}
                          </Typography>

                          {/* Ref */}
                          {infoFlags.ref && t.ref && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Ref: ${t.ref}`}
                              sx={{ height: 20 }}
                            />
                          )}

                          {/* Description snippet */}
                          {infoFlags.description && t.description?.trim() && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {(t.description || "").trim().slice(0, 120)}
                              {(t.description || "").trim().length > 120
                                ? "…"
                                : ""}
                            </Typography>
                          )}

                          {/* Reminder */}
                          {infoFlags.reminders && remText && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {remText}
                            </Typography>
                          )}

                          {/* Creator */}
                          {(() => {
                            const raw = (t.createdBy as any) || null;
                            const u = (raw?.user ?? raw) || null; // support { createdBy: { user: {...} } } and { createdBy: {...} }
                            if (!u || (typeof u.id !== "number" && !u.username))
                              return null;

                            const uname = getUserDisplayName(u);
                            const src = getUserImageUrl(u);
                            const initial = (uname?.[0] || "?").toUpperCase();

                            return (
                              <Box
                                display="inline-flex"
                                alignItems="center"
                                gap={0.5}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  By:
                                </Typography>
                                <Avatar
                                  src={src}
                                  alt={uname}
                                  sx={{ width: 18, height: 18, fontSize: 10 }}
                                >
                                  {initial}
                                </Avatar>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {uname}
                                </Typography>
                              </Box>
                            );
                          })()}

                          {/* Assignees */}
                          {(() => {
                            const safeAssignees = (
                              (t.assignedUsers ?? []) as any[]
                            )
                              .map((au) => (au?.user ?? au) || null)
                              .filter((u) => u && typeof u.id === "number");

                            if (safeAssignees.length === 0) return null;

                            return (
                              <Box display="flex" alignItems="center" gap={1}>
                                <AvatarGroup
                                  max={4}
                                  sx={{
                                    "& .MuiAvatar-root": {
                                      width: 22,
                                      height: 22,
                                      fontSize: 12,
                                    },
                                  }}
                                >
                                  {safeAssignees.map((u: any) => {
                                    const uname = getUserDisplayName(u);
                                    const src = getUserImageUrl(u);
                                    const initial = (
                                      uname[0] || "?"
                                    ).toUpperCase();
                                    return (
                                      <Tooltip key={u.id} title={uname}>
                                        <Avatar alt={uname} src={src}>
                                          {initial}
                                        </Avatar>
                                      </Tooltip>
                                    );
                                  })}
                                </AvatarGroup>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {safeAssignees
                                    .map((u: any) => getUserDisplayName(u))
                                    .join(", ")}
                                </Typography>
                              </Box>
                            );
                          })()}

                          {/* Followers (optional) */}
                          {infoFlags.followers &&
                            (() => {
                              const safeFollowers = (
                                (t.followers ?? []) as any[]
                              )
                                .map((fw) => (fw?.user ?? fw) || null)
                                .filter(
                                  (u) =>
                                    u &&
                                    (typeof u.id === "number" || u.username)
                                ) as Array<{
                                id?: number;
                                username?: string;
                                profile?: {
                                  imageUrl?: string | null;
                                } | null;
                              }>;

                              if (safeFollowers.length === 0) return null;

                              return (
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Followers:
                                  </Typography>
                                  <AvatarGroup
                                    max={4}
                                    sx={{
                                      "& .MuiAvatar-root": {
                                        width: 20,
                                        height: 20,
                                        fontSize: 11,
                                      },
                                    }}
                                  >
                                    {safeFollowers.map((u, idx) => {
                                      const key = u.id ?? `f-${idx}`;
                                      const uname =
                                        u?.username ||
                                        (u.id ? `User #${u.id}` : "User");
                                      const initial = (
                                        uname[0] || "?"
                                      ).toUpperCase();
                                      const src =
                                        u?.profile?.imageUrl || undefined;
                                      return (
                                        <Tooltip key={key} title={uname}>
                                          <Avatar alt={uname} src={src}>
                                            {initial}
                                          </Avatar>
                                        </Tooltip>
                                      );
                                    })}
                                  </AvatarGroup>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {safeFollowers.length}
                                  </Typography>
                                </Box>
                              );
                            })()}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>

            {/* ⬇️ Pagination controls */}
            {totalPages > 1 && (
              <>
                <Divider sx={{ mt: 1, mb: 1 }} />
                <Box display="flex" justifyContent="center">
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, p) => setPage(p)}
                    color="primary"
                    shape="rounded"
                    siblingCount={1}
                    boundaryCount={1}
                  />
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Info Dialog */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Project Info</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Box mb={1}>
                <Typography variant="body2" fontWeight={600}>
                  Name
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {project.name}
                </Typography>
              </Box>

              {project.description && (
                <Box mt={1}>
                  <Typography variant="body2" fontWeight={600}>
                    Description
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {project.description}
                  </Typography>
                </Box>
              )}

              <Box
                mt={1}
                display="flex"
                alignItems="center"
                gap={2}
                flexWrap="wrap"
              >
                {project.status && <Chip label={project.status} size="small" />}
                {project.priority && (
                  <Chip label={project.priority} size="small" />
                )}
                {project.defaultVisibility && (
                  <Chip
                    label={project.defaultVisibility}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>

              {project.tags?.length ? (
                <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                  {project.tags!.map((tag, i) => (
                    <Chip
                      key={`${tag}-${i}`}
                      label={tag}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              ) : null}
            </Grid>

            <Grid item xs={12} md={5}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CalendarTodayIcon fontSize="small" />
                <Typography variant="body2">Start:&nbsp;</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {project.startDate
                    ? handleDateWithTime(project.startDate)
                    : "—"}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AccessTimeIcon fontSize="small" />
                <Typography variant="body2">Deadline:&nbsp;</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {project.deadline
                    ? handleDateWithTime(project.deadline)
                    : "—"}
                </Typography>
              </Box>
              {project.department && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <BusinessIcon fontSize="small" />
                  <Typography variant="body2">Department:&nbsp;</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {project.department.name}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)}>Close</Button>
          <Button
            onClick={onOpenChat}
            startIcon={<ForumOutlinedIcon />}
            disabled={chatLoading}
          >
            Open Chat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectDetails;
